/**
 * Incidents Page - EA-Agile
 * Displays incident cards from the ea_agile.cards table (card_type='incident')
 */

import React, { useCallback, useEffect, useState } from 'react';
import { FiAlertTriangle, FiPlus, FiRefreshCw, FiFilter } from 'react-icons/fi';
import { Make_Authorized_API_Call, isSuccessResponse } from '../api/apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';
import { useStatus } from '../contexts/StatusProvider';

interface IncidentCard {
  card_id: string;
  card_type: string;
  title: string;
  description?: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  created_by: string;
  created_at: string;
  updated_at: string;
  reporter?: string;
  priority?: string;
}

type SeverityFilter = 'all' | 'critical' | 'high' | 'medium' | 'low';
type StatusFilter = 'all' | 'open' | 'in_progress' | 'resolved' | 'closed';

const IncidentsPage: React.FC = () => {
  const { Client_ID } = useStatus();
  const [incidents, setIncidents] = useState<IncidentCard[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Get effective client ID (from StatusProvider or localStorage for super admin context)
  const effectiveClientId = Client_ID || localStorage.getItem('selectedClientId');

  // Fetch incidents from API
  const fetchIncidents = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await Make_Authorized_API_Call<IncidentCard>(
        API_ENDPOINTS.EA_AGILE.CARDS,
        'GET',
        undefined,
        { params: { card_type: 'incident', Client_ID: effectiveClientId } }
      );

      if (!isSuccessResponse(response)) {
        throw new Error(response.message || 'Failed to fetch incidents');
      }

      setIncidents(response.data.Records || []);
    } catch (err) {
      console.error('Error fetching incidents:', err);
      setError(err instanceof Error ? err.message : 'Failed to load incidents');
    } finally {
      setIsLoading(false);
    }
  }, [effectiveClientId]);

  // Initial fetch and on refresh trigger
  useEffect(() => {
    fetchIncidents();
  }, [fetchIncidents, refreshTrigger]);

  // Filter incidents based on selected filters
  const filteredIncidents = incidents.filter((incident) => {
    const matchesSeverity = severityFilter === 'all' || incident.severity === severityFilter;
    const matchesStatus = statusFilter === 'all' || incident.status === statusFilter;
    return matchesSeverity && matchesStatus;
  });

  // Get severity color
  const getSeverityColor = (severity: string): string => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-500';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-500';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-500';
      case 'low':
        return 'bg-blue-100 text-blue-800 border-blue-500';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-500';
    }
  };

  // Get status badge color
  const getStatusBadgeColor = (status: string): string => {
    switch (status) {
      case 'open':
        return 'bg-red-50 text-red-700 border border-red-200';
      case 'in_progress':
        return 'bg-blue-50 text-blue-700 border border-blue-200';
      case 'resolved':
        return 'bg-green-50 text-green-700 border border-green-200';
      case 'closed':
        return 'bg-gray-50 text-gray-700 border border-gray-200';
      default:
        return 'bg-gray-50 text-gray-700 border border-gray-200';
    }
  };

  // Format status display
  const formatStatus = (status: string): string => {
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  // Format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle refresh
  const handleRefresh = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // Render loading state
  if (isLoading && incidents.length === 0) {
    return (
      <div className="p-6">
        <div className="flex items-center space-x-3 mb-6">
          <FiAlertTriangle className="h-8 w-8 text-red-600" />
          <h1 className="text-3xl font-bold text-gray-900">Incidents</h1>
        </div>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading incidents...</p>
          </div>
        </div>
      </div>
    );
  }

  // Render error state
  if (error && incidents.length === 0) {
    return (
      <div className="p-6">
        <div className="flex items-center space-x-3 mb-6">
          <FiAlertTriangle className="h-8 w-8 text-red-600" />
          <h1 className="text-3xl font-bold text-gray-900">Incidents</h1>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={handleRefresh}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            <FiRefreshCw className="h-4 w-4" />
            <span>Retry</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <FiAlertTriangle className="h-8 w-8 text-red-600" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Incidents</h1>
              <p className="text-gray-600 mt-1">
                Monitor and manage system incidents
              </p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center space-x-3">
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Refresh incidents list"
            >
              <FiRefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
            <button
              className="inline-flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              title="Create new incident"
            >
              <FiPlus className="h-4 w-4" />
              <span>Create Incident</span>
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-600">Total Incidents</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{incidents.length}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-600">Open</div>
            <div className="text-2xl font-bold text-red-600 mt-1">
              {incidents.filter(i => i.status === 'open').length}
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-600">In Progress</div>
            <div className="text-2xl font-bold text-blue-600 mt-1">
              {incidents.filter(i => i.status === 'in_progress').length}
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-600">Resolved</div>
            <div className="text-2xl font-bold text-green-600 mt-1">
              {incidents.filter(i => i.status === 'resolved').length}
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex items-center space-x-3 mb-4">
          <FiFilter className="h-5 w-5 text-gray-600" />
          <h3 className="text-sm font-semibold text-gray-900">Filters</h3>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Severity filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Severity
            </label>
            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value as SeverityFilter)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="all">All Severities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>

          {/* Status filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="all">All Statuses</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Incidents List */}
      <div className="space-y-4">
        {filteredIncidents.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <FiAlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No incidents found</h3>
            <p className="text-gray-600 mb-6">
              {incidents.length === 0
                ? 'No incidents recorded. Great job!'
                : 'No incidents match the selected filters.'}
            </p>
            {incidents.length === 0 && (
              <button className="inline-flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
                <FiPlus className="h-4 w-4" />
                <span>Create First Incident</span>
              </button>
            )}
          </div>
        ) : (
          filteredIncidents.map((incident) => (
            <div
              key={incident.card_id}
              className="bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow overflow-hidden cursor-pointer"
            >
              <div className="p-6">
                {/* Header row */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {incident.title}
                      </h3>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getSeverityColor(incident.severity)}`}>
                        {incident.severity.toUpperCase()}
                      </span>
                    </div>
                    {incident.description && (
                      <p className="text-gray-600 line-clamp-2">
                        {incident.description}
                      </p>
                    )}
                  </div>
                </div>

                {/* Metadata row */}
                <div className="grid grid-cols-4 gap-4 mb-4">
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Status</div>
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getStatusBadgeColor(incident.status)}`}>
                      {formatStatus(incident.status)}
                    </span>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Reporter</div>
                    <div className="text-sm font-medium text-gray-900">
                      {incident.reporter || incident.created_by}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Created</div>
                    <div className="text-sm text-gray-900">
                      {formatDate(incident.created_at)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Updated</div>
                    <div className="text-sm text-gray-900">
                      {formatDate(incident.updated_at)}
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="text-xs text-gray-500">
                    ID: {incident.card_id}
                  </div>
                  <button className="px-3 py-1 text-sm text-blue-600 hover:text-blue-700 font-medium">
                    View Details →
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Legend */}
      <div className="mt-8 bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Severity Levels</h3>
        <div className="grid grid-cols-4 gap-4 text-xs">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <span className="text-gray-600">Critical - Immediate action required</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-orange-500"></div>
            <span className="text-gray-600">High - Should be addressed soon</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <span className="text-gray-600">Medium - Can be scheduled</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
            <span className="text-gray-600">Low - Future consideration</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncidentsPage;
